import { GRAPH_TYPES } from '../constants';
import { JAVA_API_STATUS, UW_SUCCESS_STATUS_CODE } from '../../../utils/axios-interceptor';
import { DATE_FORMATS, formatDateByFormat } from '../../../utils/dateUtils';
import { inrAmount } from '../../../utils/formatters/inrAmount';
import {
    MSME_DELAY_CARD_HEADER,
    MSME_DELAY_LEGEND,
    MSME_DELAY_SECONDARY_PREFIX,
    MSME_DELAYS_TILE_DURATION_PREFIX,
    MSME_DELAYS_TILE_TITLE,
    MSME_PLOT_LEGENDS,
} from './constants';
import type {
    BefeResponseBody,
    EpfFilingDetails,
    EpfPayment,
    MsmeDelayGraph,
    MsmeDelayGraphMetadata,
    MsmeDelaysHeadlineCard,
    UwEpfFilingDetailsData,
    UwMsmePaymentDelaysData,
    UwServiceEntry,
} from './types';

/** polus re-serialises UW numbers (possibly as "1.9E7"); always read them numerically. */
export const toNumberOrNull = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
};

/**
 * The UW payload of one BEFE service, or null on any failure. BEFE swallows UW errors into an
 * empty entry and still answers responseCode 20, so the UW statusCode is the real signal.
 * UW sends `InsightsAppResponse`, so the payload is exactly ONE `.data` deep.
 */
export function readUwData<T>(body: unknown, serviceId: string): T | null {
    const befeBody = body as BefeResponseBody | null | undefined;
    if (toNumberOrNull(befeBody?.responseData?.responseCode) !== JAVA_API_STATUS.SUCCESS) return null;
    return readUwEntryData(befeBody?.responseBody?.[serviceId] as UwServiceEntry<T> | undefined);
}

export function toEpfFilingDetails(data: UwEpfFilingDetailsData): EpfFilingDetails {
    const paymentsByXValue: Record<string, EpfPayment[]> = {};
    (data.payments ?? []).forEach((payment) => {
        const xValue = payment.period;
        if (!xValue) return;
        if (!paymentsByXValue[xValue]) paymentsByXValue[xValue] = [];
        paymentsByXValue[xValue].push({
            establishmentId: payment.establishmentId ?? null,
            numberOfEmployee: toNumberOrNull(payment.numberOfEmployee),
            totalAmount: toNumberOrNull(payment.totalAmount),
            delayInDays: toNumberOrNull(payment.delayInDays),
            isDefault: !!payment.isDefault,
            dateOfCredit: payment.dateOfCredit ?? null,
        });
    });
    const establishments = (data.establishments ?? []).map((establishment) => ({
        ...establishment,
        amount: toNumberOrNull(establishment.amount),
        noOfEmployees: toNumberOrNull(establishment.noOfEmployees),
    }));
    return { paymentsByXValue, establishments };
}

const formatPeriodBound = (isoDate?: string): string => formatDateByFormat(isoDate, DATE_FORMATS.PERIOD_MONTH_YEAR);

export function toMsmeDelayGraph(data: UwMsmePaymentDelaysData): MsmeDelayGraph {
    const { metaData } = data;
    const metadata: MsmeDelayGraphMetadata = {
        cardType: GRAPH_TYPES.MSME_DELAY,
        ...MSME_DELAY_CARD_HEADER,
        legend: MSME_DELAY_LEGEND,
    };
    const latestAmount = readLatestPeriodAmount(metaData);
    if (latestAmount !== null) {
        metadata.primaryText = `₹${inrAmount(Math.round(latestAmount))}`;
        metadata.secondaryText = `${MSME_DELAY_SECONDARY_PREFIX}${formatLatestPeriod(metaData)}`;
    }

    return {
        metadata,
        plots: [
            {
                legend: MSME_PLOT_LEGENDS.PERIODS,
                plotValues: (data.periods ?? []).map((period) => ({
                    xValue: (period.periodStart ?? '').slice(0, 7),
                    name: period.periodLabel,
                    value: toNumberOrNull(period.amount),
                })),
            },
            {
                legend: MSME_PLOT_LEGENDS.SUPPLIERS,
                plotValues: (data.suppliers ?? []).map((supplier) => ({
                    id: supplier.supplierPan ?? null,
                    name: supplier.supplierName ?? null,
                    value: toNumberOrNull(supplier.amountDue),
                })),
            },
        ],
        headlineCard: toMsmeDelaysHeadlineCard(metaData),
    };
}

export function toMsmeDelaysHeadlineCard(metaData: UwMsmePaymentDelaysData['metaData']): MsmeDelaysHeadlineCard {
    const card: MsmeDelaysHeadlineCard = { title: MSME_DELAYS_TILE_TITLE };
    const latestAmount = readLatestPeriodAmount(metaData);
    if (latestAmount !== null) card.value = inrAmount(Math.round(latestAmount));
    if (metaData?.latestPeriodStart && metaData?.latestPeriodEnd) {
        card.metricDuration = `${MSME_DELAYS_TILE_DURATION_PREFIX}${formatLatestPeriod(metaData)}`;
    }
    return card;
}

function formatLatestPeriod(metaData: UwMsmePaymentDelaysData['metaData']): string {
    return `${formatPeriodBound(metaData?.latestPeriodStart)} - ${formatPeriodBound(metaData?.latestPeriodEnd)}`;
}

function readLatestPeriodAmount(metaData: UwMsmePaymentDelaysData['metaData']): number | null {
    return (metaData?.periodCount ?? 0) > 0 ? toNumberOrNull(metaData?.latestPeriodAmount) : null;
}

/** The payload of one UW service entry, or null unless UW reported success with data. */
export function readUwEntryData<T>(entry: UwServiceEntry<T> | null | undefined): T | null {
    if (toNumberOrNull(entry?.statusCode) !== UW_SUCCESS_STATUS_CODE || !entry?.data) return null;
    return entry.data;
}
